import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '../../lib/auth';

interface AuthGuardProps {
  children: React.ReactNode;
  /** If set, also require the user to have one of these roles. */
  requireRole?: ('owner' | 'admin' | 'super_admin')[];
  /**
   * If true, allow access even when the user has must_change_password=true.
   * Set this on /change-password so the user can actually reach the page that
   * lets them clear the flag. Default false: redirects to /change-password
   * when the flag is set.
   */
  allowPasswordChangePending?: boolean;
}

/**
 * Wraps a route element. Redirects to /login if not authenticated. If the
 * user has must_change_password=true and this guard does not opt out, sends
 * them to /change-password. Optionally also gates by role.
 *
 * Mirrors the server-side rules in the PocketBase schema but provides UX
 * feedback before the API rejects the request.
 */
export function AuthGuard({
  children,
  requireRole,
  allowPasswordChangePending,
}: AuthGuardProps): JSX.Element {
  const { isAuthenticated, mustChangePassword, user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!isAuthenticated) {
      document.title = 'Sign in required | Sunset Harbor Condominiums';
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (mustChangePassword && !allowPasswordChangePending) {
    return <Navigate to="/change-password" replace />;
  }

  if (requireRole && requireRole.length > 0) {
    if (!user || !requireRole.includes(user.role)) {
      return <Navigate to="/portal" replace />;
    }
  }

  return <>{children}</>;
}
